import { usePaymentContext } from "lib/contexts/PaymentProvider";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { useLocale } from "lib/hooks/useLocale";
import { PaymentMode, UpiIntentApps } from "lib/types/payments";
import { MethodLogo, UpiIntentItems } from "@shopflo/ui/payments/upi-intent";
import React, { useMemo } from "react";
import useSWR from "swr";
import { staticOptions } from "lib/core/apiClient";
import { getUpiAppLink, getUpiIntentModeConfig } from "lib/utils/payments";
import { useIsSubscribeMode } from "lib/hooks/useIsSubscribeMode";
import { analyticsEvents, analyticsTypes } from "lib/utils/constants";
import { getAutomationPayloadForMethod, isMobile, redirectUrl } from "lib/utils/helpers";
import { DialogBody, DialogHeader, GenericDialog } from "components/common/dialogs/GenericDialog";
import { X } from "@phosphor-icons/react";
import { getPaymentMethodAction } from "lib/core/apiMethods";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import InputField from "components/common/forms/InputField";
import useForm, { FormField } from "lib/hooks/useForm";
import PrimaryButton from "components/common/buttons/PrimaryButton";
import { errorToast } from "lib/utils/toasters";

const UPIIntent = () => {
  const { t } = useLocale();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();

  const {
    actions: { setPaymentMethodDialog, setPaymentState },
    state: { openPaymentMethodDialog, paymentMethods },
  } = usePaymentContext();

  const {
    state: { checkoutId },
    actions: { setCheckoutExpired },
  } = useCheckoutContext();

  const {
    state: { merchant, checkoutMetadata },
  } = useMerchantContext();
  const isSubscribeMode = useIsSubscribeMode();

  const upiIntent = useMemo(
    () => paymentMethods?.find((paymentMethod: PaymentMode) => paymentMethod.name === "UPI_INTENT"),
    [paymentMethods],
  );

  const upiApps: UpiIntentItems[] = useMemo(() => {
    const modeConfig = getUpiIntentModeConfig(checkoutMetadata, isSubscribeMode);

    return modeConfig?.selected_apps?.filter((item) => item.is_active) ?? [];
  }, [checkoutMetadata, isSubscribeMode]);

  const {
    data: upiIntentUrl,
    error: responseErr,
    isValidating: isLoading,
  } = useSWR(
    openPaymentMethodDialog === "UPI_INTENT" && isMobile() ? `UPI_INTENT` : null,
    (mode) =>
      getPaymentMethodAction({
        method: mode,
        checkoutId,
        setCheckoutExpired,
        context: upiIntent?.context,
        useKratosPaymentFlow: Boolean(merchant?.migrationConfig?.kratosPaymentFlow),
      }),
    staticOptions,
  );

  const openUPIApp = (upiApp: UpiIntentApps) => {
    if (!Boolean(upiIntentUrl) || isLoading) {
      return;
    }
    const url = upiIntentUrl?.next_steps?.pg_action?.action_url;
    const upiAppLink = getUpiAppLink(upiApp, url ?? "");

    if (!Boolean(upiAppLink)) return;
    const methodData = paymentMethods?.find((e: PaymentMode) => e.name === "UPI_INTENT");

    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_PAYMENT_METHOD_SELECTED,
      eventFor: [analyticsTypes.SF_ANALYTICS, analyticsTypes.FACEBOOK_PIXEL],
      eventType: "click",
      metaData: {
        methodType: "upi_intent",
        automationData: getAutomationPayloadForMethod(methodData),
      },
    });
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_PAYMENT_MODE_SELECTED,
      eventType: "click",
      metaData: {
        modeType: upiApp,
        methodType: "upi_intent",
        automationData: getAutomationPayloadForMethod(methodData),
      },
    });

    setPaymentState({
      isProcessingDialogVisible: true,
      paymentStatus: "PROCESSING",
      isPolling: true,
    });

    redirectUrl(upiAppLink);
  };

  const {
    handleSubmit,
    setErrors,
    inputProps,
    state: { values, errors },
  } = useForm({
    initialState: { id: "", save: false },
    validationSchema: {
      id: {
        required: t("invalid_upi"),
        type: "string",
        when: (value: string) => {
          const upiRegex = /[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}/;
          const isValid = upiRegex.test(value);
          if (!isValid) {
            return {
              status: true,
              message: t("invalid_upi"),
            };
          }
        },
      },
    },
  });

  const tileTitle = useMemo(() => {
    const upiIntent = paymentMethods.find((method: PaymentMode) => method?.name === "UPI_INTENT");
    return upiIntent?.metadata?.title ?? "Pay using UPI";
  }, [paymentMethods]);

  return (
    <GenericDialog
      isOpen={openPaymentMethodDialog === "UPI_INTENT"}
      setIsOpen={() => setPaymentMethodDialog("NONE")}
      translateAxis="y"
      customClass="overflow-scroll md:!top-auto md:absolute rounded-t-2xl max-h-[81vh]"
      modalType="UPI_INTENT"
      dialogOverlay={true}>
      <DialogHeader>
        <h3 className="flex flex-row items-center justify-between w-full h-full py-4 text-base font-medium bg-white text-carbon-dark">
          <div className="flex items-center">
            <h2 className="inline-flex gap-2 font-medium text-carbon-dark">{tileTitle}</h2>
          </div>
          <button className="outline-none">
            <X
              className="w-6 h-6 cursor-pointer text-coal-dark"
              onClick={() => setPaymentMethodDialog("NONE")}
            />
          </button>
        </h3>
      </DialogHeader>
      <DialogBody className="flex flex-col px-3 pb-36">
        <>
          {isMobile() && !responseErr && upiIntentUrl && (
            <div className="box-border w-full">
              <div className="flex w-full flex-col rounded-2xl border border-[#F0F0F0]">
                <ul className="flex flex-row items-center justify-around w-full py-4">
                  {upiApps?.map((method) => (
                    <li
                      key={method?.id?.toUpperCase()}
                      onClick={() => openUPIApp(method?.id?.toUpperCase() as UpiIntentApps)}>
                      <MethodLogo
                        id={method.id}
                        logo={method.url}
                        name={method.name}
                        offerText={method.offer_text}
                        isLoading={isLoading}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </>
      </DialogBody>
    </GenericDialog>
  );
};

export default UPIIntent;
